import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  BookOpen,
  Monitor,
  GraduationCap,
  Briefcase,
  Building,
  RefreshCcw,
  Landmark,
  Smartphone,
  Repeat,
  Paperclip,
  User,
  CheckSquare,
  CreditCard,
  Printer,
  Keyboard,
  CheckCircle,
  MapPin,
  Phone,
  Clock,
  ArrowRight,
  ChevronRight,
  MessageCircle,
  Star,
  Shield,
  Zap,
  Info,
} from "lucide-react";

// --- DATA ---
const WHATSAPP_NUMBER = "2348064539946";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;
const DEADLINE_DATE = new Date("May 2, 2026 23:59:59").getTime();

const services = [
  {
    title: "INEC Revalidation",
    desc: "Revalidate your INEC voter records before the deadline. Status verification and online filing.",
    icon: User,
  },
  {
    title: "JAMB Registration",
    desc: "UTME registration correctly and on time. Scratch card, profile creation and full registration handled here.",
    icon: GraduationCap,
  },
  {
    title: "CBT Practice",
    desc: "Practise on a real computer before exam day. Same timing, same format as the real thing.",
    icon: Monitor,
  },
  {
    title: "WAEC, NECO & NABTEB",
    desc: "We register schools and individual students for all major examinations. No errors, no delays.",
    icon: BookOpen,
  },
  {
    title: "NYSC Registration",
    desc: "We help fresh graduates complete NYSC mobilisation and portal registration step by step.",
    icon: Briefcase,
  },
  {
    title: "CAC Business Registration",
    desc: "Register your business name, limited company, NGO, church, or foundation with the CAC.",
    icon: Building,
  },
  {
    title: "NIN Enrolment & Update",
    desc: "New NIN registration and modifications to existing NIN records done quickly and professionally.",
    icon: Smartphone,
  },
  {
    title: "NIN Retrieval & Reprinting",
    desc: "Lost your NIN slip or forgotten your number? We help you retrieve and print a fresh copy.",
    icon: RefreshCcw,
  },
  {
    title: "BVN Registration",
    desc: "Register your Bank Verification Number without the usual bank stress and long queues.",
    icon: Landmark,
  },
  {
    title: "SIM Card Registration",
    desc: "We register new SIM cards for all networks (MTN, Airtel, Glo, 9mobile) quickly and correctly.",
    icon: Smartphone,
  },
  {
    title: "SIM Swap & Welcome Back",
    desc: "Process your Welcome Back and SIM swap to get your old, lost or damaged number restored.",
    icon: Repeat,
  },
  {
    title: "NIN and SIM Linkage",
    desc: "Is your SIM card blocked because it is not linked to your NIN? We sort it out on the spot.",
    icon: Paperclip,
  },
  {
    title: "NPC Birth Attestation",
    desc: "We process your National Population Commission birth attestation and certificate applications.",
    icon: User,
  },
  {
    title: "Result Checking",
    desc: "Check and verify JAMB, WAEC, NECO and NABTEB results quickly and print them out.",
    icon: CheckSquare,
  },
  {
    title: "OPay POS & Cards",
    desc: "We sell and deploy OPay POS terminals and ATM cards for business owners and individuals.",
    icon: CreditCard,
  },
  {
    title: "Printing & Photocopy",
    desc: "Print, photocopy and laminate all kinds of documents. Fast, clean, and highly affordable.",
    icon: Printer,
  },
  {
    title: "Typing & Data Entry",
    desc: "Professional typing, data processing and document formatting delivered accurately and on time.",
    icon: Keyboard,
  },
];

const reasons = [
  "Over 10 Years of Experience — We have been doing this since 2014. We know exactly what to do.",
  "We Are Fast — You do not wait unnecessarily. You come in and we sort it out.",
  "We Are Affordable — Quality service at a price that makes sense for everyone.",
  "We Respect Every Single Customer — No matter who you are or what you need, you will be treated with respect.",
  "We Are Reliable — When we say it will be done, it gets done.",
  "Remote Services Available — Some services can be handled without you coming in. Just reach out on WhatsApp.",
];

// --- COMPONENTS ---

const RegistrationWeb = () => {
  // Define positions for the main connecting nodes (percentages)
  const nodes = [
    {
      id: "jamb",
      x: 50,
      y: 12,
      img: "https://i.ibb.co/YBL7TgnD/Official-JAMB-logo.png",
      alt: "JAMB",
      glow: "shadow-green-500/30",
    },
    {
      id: "waec",
      x: 88,
      y: 35,
      img: "https://i.ibb.co/BKyNwyMP/waec.png",
      alt: "WAEC",
      glow: "shadow-blue-500/30",
    },
    {
      id: "nysc",
      x: 80,
      y: 80,
      img: "https://i.ibb.co/4B2fCYQ/nysc.png",
      alt: "NYSC",
      glow: "shadow-green-600/30",
    },
    {
      id: "cac",
      x: 50,
      y: 88,
      img: "https://i.ibb.co/jP8RY8fY/CAC.png",
      alt: "CAC",
      glow: "shadow-slate-500/30",
    },
    {
      id: "inec",
      x: 20,
      y: 80,
      img: "https://i.ibb.co/f77t6HX/inec.png",
      alt: "INEC",
      glow: "shadow-emerald-600/30",
    },
    {
      id: "neco",
      x: 12,
      y: 35,
      img: "https://i.ibb.co/nMkZ06HH/neco.png",
      alt: "NECO",
      glow: "shadow-emerald-500/30",
    },
  ];

  // Secondary decorative nodes to make the "web" look complex
  const secNodes = [
    { x: 30, y: 28 },
    { x: 70, y: 28 },
    { x: 85, y: 65 },
    { x: 15, y: 65 },
    { x: 50, y: 70 },
  ];

  return (
    <div className="relative w-full aspect-square max-w-[500px] mx-auto mt-10 lg:mt-0">
      {/* SVG Connections Layer */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ zIndex: 0 }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Main lines connecting center to logos */}
        {nodes.map((n, i) => (
          <line
            key={`c-${i}`}
            x1="50%"
            y1="50%"
            x2={`${n.x}%`}
            y2={`${n.y}%`}
            stroke="#4ade80"
            strokeWidth="2"
            strokeDasharray="6 6"
            className="animate-pulse"
          />
        ))}

        {/* Perimeter web lines connecting logos to each other */}
        {nodes.map((n, i) => {
          const next = nodes[(i + 1) % nodes.length];
          return (
            <line
              key={`p-${i}`}
              x1={`${n.x}%`}
              y1={`${n.y}%`}
              x2={`${next.x}%`}
              y2={`${next.y}%`}
              stroke="rgba(74, 222, 128, 0.2)"
              strokeWidth="1"
            />
          );
        })}

        {/* Secondary Decorative Web Elements */}
        {secNodes.map((sn, i) => (
          <g key={`sec-${i}`}>
            <circle
              cx={`${sn.x}%`}
              cy={`${sn.y}%`}
              r="3"
              fill="#4ade80"
              opacity="0.4"
            />
            <line
              x1="50%"
              y1="50%"
              x2={`${sn.x}%`}
              y2={`${sn.y}%`}
              stroke="rgba(74, 222, 128, 0.15)"
              strokeWidth="1"
            />
            <line
              x1={`${sn.x}%`}
              y1={`${sn.y}%`}
              x2={`${nodes[i].x}%`}
              y2={`${nodes[i].y}%`}
              stroke="rgba(74, 222, 128, 0.15)"
              strokeWidth="1"
            />
          </g>
        ))}
      </svg>

      {/* Central Hub (Binary Digital Ventures Pulse) */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
        <div className="relative flex items-center justify-center">
          {/* Pulsing rings */}
          <div
            className="absolute w-24 h-24 md:w-32 md:h-32 bg-green-500 rounded-full animate-ping opacity-20"
            style={{ animationDuration: "3s" }}
          ></div>
          <div className="absolute w-28 h-28 md:w-36 md:h-36 border border-green-500/30 rounded-full animate-spin-slow"></div>

          {/* Core Hub */}
          <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-900 rounded-full border-4 border-green-400 shadow-[0_0_30px_rgba(34,197,94,0.6)] flex items-center justify-center relative z-10">
            <div className="absolute inset-0 flex items-center justify-center">
              <Monitor
                className="text-slate-600 w-8 h-8 md:w-10 md:h-10 opacity-40"
                fill="currentColor"
              />
            </div>
            <svg
              viewBox="0 0 24 24"
              className="w-6 h-6 md:w-8 md:h-8 text-green-400 relative z-10"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M5 12.55a11 11 0 0 1 14.08 0" />
              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" />
            </svg>
          </div>
        </div>
      </div>

      {/* Registration Logos (Connected Nodes) */}
      {nodes.map((n) => (
        <div
          key={n.id}
          className={`absolute w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-white rounded-2xl shadow-xl border-2 border-slate-700 flex flex-col items-center justify-center p-2 z-10 transform -translate-x-1/2 -translate-y-1/2 hover:scale-110 transition-transform duration-300 hover:border-green-400 ${n.glow}`}
          style={{ top: `${n.y}%`, left: `${n.x}%` }}
        >
          <img
            src={n.img}
            alt={n.alt}
            className="w-full h-8 sm:h-10 md:h-12 object-contain mb-1"
          />
          <span className="text-[8px] sm:text-[9px] md:text-[10px] font-bold text-slate-600 uppercase tracking-wider">
            {n.alt}
          </span>
        </div>
      ))}
    </div>
  );
};

// Fixed explicit typing for 'value' and 'label'
const TimeBox = ({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-col items-center bg-slate-900 border border-emerald-500/30 p-3 md:p-4 rounded-xl min-w-[65px] md:min-w-[80px]">
    <span className="text-2xl md:text-3xl font-bold text-white">
      {value.toString().padStart(2, "0")}
    </span>
    <span className="text-[10px] md:text-xs text-emerald-400 font-bold uppercase tracking-wider mt-1">
      {label}
    </span>
  </div>
);

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = DEADLINE_DATE - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          ),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex space-x-2 md:space-x-4">
      <TimeBox value={timeLeft.days} label="Days" />
      <TimeBox value={timeLeft.hours} label="Hours" />
      <TimeBox value={timeLeft.minutes} label="Mins" />
      <TimeBox value={timeLeft.seconds} label="Secs" />
    </div>
  );
};

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(
    Date.now() >= DEADLINE_DATE
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check for deadline passing
  useEffect(() => {
    if (isDeadlinePassed) return;
    const interval = setInterval(() => {
      if (Date.now() >= DEADLINE_DATE) {
        setIsDeadlinePassed(true);
        setShowPopup(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isDeadlinePassed]);

  // Show popup shortly after site load
  useEffect(() => {
    if (isDeadlinePassed) return;
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [isDeadlinePassed]);

  // Fixed explicit typing for 'id'
  const scrollTo = (id: string) => {
    setIsMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="font-sans text-slate-800 bg-slate-50 min-h-screen">
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 12s linear infinite;
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.4s ease-out forwards;
        }
      `,
        }}
      />

      {/* INEC ENTRY POPUP */}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-emerald-500/50 rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-[0_0_40px_rgba(16,185,129,0.2)] relative animate-fade-in-up">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 p-2 rounded-full transition-colors z-10"
            >
              <X size={20} />
            </button>

            <div className="flex items-center mb-5">
              <img
                src="https://i.ibb.co/f77t6HX/inec.png"
                alt="INEC"
                className="w-12 h-12 mr-4 object-contain drop-shadow-md"
              />
              <div className="inline-block px-3 py-1.5 rounded-full bg-red-500/20 text-red-400 text-xs font-bold border border-red-500/30 uppercase tracking-wider animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.2)]">
                Urgent Deadline
              </div>
            </div>

            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">
              INEC Revalidation is{" "}
              <span className="text-emerald-400">Closing Soon</span>
            </h3>

            <p className="text-slate-300 mb-6 font-medium leading-relaxed">
              Don't lose your right to vote! The LGA phase deadline is May 2nd.
              Revalidate your voter records today to remain eligible for the
              2027 General Elections.
            </p>

            <div className="mb-8 bg-slate-800/80 rounded-2xl p-5 border border-slate-700 shadow-inner">
              <p className="text-xs text-emerald-400 font-bold mb-3 uppercase tracking-wider text-center">
                Time Remaining To Revalidate
              </p>
              <div className="flex justify-center scale-90 sm:scale-100 origin-center">
                <CountdownTimer />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                onClick={() => setShowPopup(false)}
                className="flex-1 flex items-center justify-center px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full font-extrabold text-center transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)]"
              >
                Revalidate Now <ChevronRight className="w-5 h-5 ml-1" />
              </a>
              <button
                onClick={() => {
                  setShowPopup(false);
                  scrollTo("inec");
                }}
                className="px-6 py-3.5 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 rounded-full font-bold text-center transition-colors"
              >
                Read More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NAVIGATION */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-md py-3" : "bg-white/95 py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => window.scrollTo(0, 0)}
            >
              <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mr-3 shadow-lg border-2 border-slate-300 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Monitor
                    className="text-slate-600 w-8 h-8 opacity-50"
                    fill="currentColor"
                  />
                </div>
                <svg
                  viewBox="0 0 24 24"
                  className="w-7 h-7 text-green-400 relative z-10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                  <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                  <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                  <line x1="12" y1="20" x2="12.01" y2="20" />
                </svg>
              </div>
              <div>
                <h1 className="font-bold text-xl md:text-2xl tracking-tight text-slate-900 leading-none">
                  Binary Digital Ventures
                </h1>
                <p className="text-xs text-green-600 font-bold tracking-widest mt-1">
                  DONE RIGHT.
                </p>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollTo("about")}
                className="text-sm font-semibold text-slate-600 hover:text-green-600 transition-colors"
              >
                About Us
              </button>
              <button
                onClick={() => scrollTo("services")}
                className="text-sm font-semibold text-slate-600 hover:text-green-600 transition-colors"
              >
                Services
              </button>
              {!isDeadlinePassed && (
                <button
                  onClick={() => scrollTo("inec")}
                  className="text-sm font-bold text-emerald-600 hover:text-emerald-700 transition-colors flex items-center"
                >
                  <Info size={16} className="mr-1" /> INEC Update
                </button>
              )}
              <button
                onClick={() => scrollTo("cac")}
                className="text-sm font-semibold text-slate-600 hover:text-green-600 transition-colors"
              >
                CAC
              </button>
              <button
                onClick={() => scrollTo("cbt")}
                className="text-sm font-semibold text-slate-600 hover:text-green-600 transition-colors"
              >
                CBT Practice
              </button>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white text-sm font-bold rounded-full transition-all shadow-md hover:shadow-lg shadow-green-500/30"
              >
                Get Started
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-slate-600 hover:text-slate-900 p-2"
              >
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-xl border-t border-slate-100 py-4 px-4 flex flex-col space-y-4">
            <button
              onClick={() => scrollTo("about")}
              className="text-left py-2 text-slate-700 font-medium"
            >
              About Us
            </button>
            <button
              onClick={() => scrollTo("services")}
              className="text-left py-2 text-slate-700 font-medium"
            >
              All Services
            </button>
            {!isDeadlinePassed && (
              <button
                onClick={() => scrollTo("inec")}
                className="text-left py-2 text-emerald-600 font-bold flex items-center"
              >
                <Info size={18} className="mr-2" /> INEC Revalidation
              </button>
            )}
            <button
              onClick={() => scrollTo("cac")}
              className="text-left py-2 text-slate-700 font-medium"
            >
              CAC Registration
            </button>
            <button
              onClick={() => scrollTo("cbt")}
              className="text-left py-2 text-slate-700 font-medium"
            >
              CBT Practice
            </button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="text-center py-3 bg-green-500 text-white rounded-lg font-bold w-full"
            >
              Chat on WhatsApp
            </a>
          </div>
        )}
      </nav>

      {/* HERO SECTION - REDESIGNED WITH CONNECTED WEB */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column: Text & CTA */}
            <div className="text-left">
              <div className="inline-block mb-6 px-5 py-2 rounded-full bg-green-900/40 border border-green-500/50 text-green-400 text-sm font-bold tracking-wide backdrop-blur-md shadow-[0_0_15px_rgba(34,197,94,0.3)]">
                <Star className="inline-block w-4 h-4 mr-2 -mt-1" /> Fast.
                Affordable. Done Right.
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6 leading-tight">
                The Number One <span className="text-green-400">WAEC</span>,{" "}
                <span className="text-green-400">JAMB</span>,{" "}
                <span className="text-green-400">NECO</span>,{" "}
                <span className="text-green-400">NYSC</span> &{" "}
                <span className="text-green-400">CAC</span> Registration Centre
                in Orlu
              </h1>

              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 font-medium max-w-xl">
                Binary Digital Ventures handles your digital registrations,
                documents, printing and online services quickly, correctly and
                at a price you can afford.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-auto px-8 py-4 bg-green-500 hover:bg-green-400 text-slate-900 rounded-full font-extrabold text-lg transition-all shadow-[0_0_20px_rgba(34,197,94,0.4)] flex items-center justify-center"
                >
                  Get Started Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <button
                  onClick={() => scrollTo("services")}
                  className="w-full sm:w-auto px-8 py-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full font-bold text-lg transition-all backdrop-blur-sm"
                >
                  See All Services
                </button>
              </div>
            </div>

            {/* Right Column: Interactive Web Layout */}
            <RegistrationWeb />
          </div>
        </div>
      </section>

      {/* ABOUT SECTION WITH STATS CAROUSEL */}
      <section id="about" className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                We Have Been Here and We Know What We Are Doing
              </h2>
              <div className="w-20 h-1.5 bg-green-500 rounded-full mb-8"></div>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  Binary Digital Ventures is located at{" "}
                  <strong>No. 169 B.S.C. Road, Orlu, Imo State</strong>. We have
                  been in business since 2014 and have helped thousands of
                  people across Orlu and Imo State get their registrations and
                  documents sorted out.
                </p>
                <p>
                  We help students register for exams, graduates complete NYSC,
                  business owners register with CAC and everyday people sort out
                  their NIN, BVN and SIM issues. Whatever you need, we handle it
                  with speed, accuracy and respect.
                </p>
                <div className="p-6 bg-slate-50 rounded-2xl border-l-4 border-green-500 shadow-sm">
                  <p className="text-xl font-medium text-slate-800 italic">
                    "We built this business on one word:{" "}
                    <span className="text-green-600 font-bold">integrity</span>.
                    That is still what we stand for today."
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl relative bg-slate-800">
                <img
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
                  alt="Business Professionals"
                  className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl text-white shadow-xl">
                    <div className="text-5xl font-extrabold mb-1 text-green-400">
                      10+
                    </div>
                    <div className="text-lg font-bold text-white uppercase tracking-wider">
                      Years of Trusted Service
                    </div>
                    <div className="text-sm text-slate-300 mt-2">
                      Established 2014
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-green-100 rounded-full -z-10 blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-slate-200 rounded-full -z-10 blur-2xl"></div>
            </div>
          </div>
        </div>

        {/* Feature Highlights / Stats Carousel */}
        <div className="bg-slate-900 py-8 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 flex items-center shadow-lg transform hover:-translate-y-1 transition-all">
                <Shield className="w-12 h-12 text-green-400 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-bold text-lg">
                    Error-Free Registrations
                  </h4>
                  <p className="text-slate-400 text-sm mt-1">
                    100% accuracy on all your critical documents and forms.
                  </p>
                </div>
              </div>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 flex items-center shadow-lg transform hover:-translate-y-1 transition-all">
                <Zap className="w-12 h-12 text-green-400 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-bold text-lg">
                    Fast AI-Powered Typing
                  </h4>
                  <p className="text-slate-400 text-sm mt-1">
                    Lightning fast. We can type and format 20 pages in just 5
                    minutes!
                  </p>
                </div>
              </div>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 flex items-center shadow-lg transform hover:-translate-y-1 transition-all">
                <CheckCircle className="w-12 h-12 text-green-400 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-bold text-lg">
                    Thousands of Happy Clients
                  </h4>
                  <p className="text-slate-400 text-sm mt-1">
                    The most trusted centre across Orlu and Imo State.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="services" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              What We Do For You
            </h2>
            <p className="text-lg text-slate-600 font-medium">
              From major exams to essential ID registrations, our comprehensive
              services are designed to make your life easier.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services
              .filter(
                (s) => !(isDeadlinePassed && s.title === "INEC Revalidation")
              )
              .map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-green-300 transition-all duration-300 group relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-20 h-20 bg-green-50 rounded-bl-full -z-10 group-hover:bg-green-100 transition-colors duration-300"></div>

                    {/* Premium Icon Styling with Fallback */}
                    <div className="w-14 h-14 bg-slate-900 text-green-400 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                      {Icon ? <Icon size={26} strokeWidth={2} /> : null}
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-sm font-medium">
                      {service.desc}
                    </p>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* INEC REVALIDATION SPOTLIGHT */}
      {!isDeadlinePassed && (
        <section
          id="inec"
          className="py-20 bg-emerald-950 text-white overflow-hidden relative border-t-8 border-emerald-500"
        >
          <div className="absolute top-0 right-0 w-full h-full opacity-5 pointer-events-none">
            <svg
              className="absolute w-full h-full"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern
                  id="grid-pattern-inec"
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid-pattern-inec)" />
            </svg>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-900 text-emerald-400 text-sm font-bold mb-6 border border-emerald-700/50 shadow-sm">
                  <img
                    src="https://i.ibb.co/f77t6HX/inec.png"
                    alt="INEC Logo"
                    className="w-5 h-5 mr-2 object-contain"
                  />{" "}
                  Urgent Service Update
                </div>

                <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                  Fast & Easy <span className="text-emerald-400">INEC</span>{" "}
                  Revalidation
                </h2>
                <p className="text-xl text-emerald-100 mb-6 font-medium leading-relaxed">
                  <strong className="text-white">
                    Don’t lose your right to vote.
                  </strong>{" "}
                  INEC has mandated that all voters registered between 2011 and
                  2024 must revalidate their records to remain eligible for the
                  2027 General Elections.
                </p>
                <p className="text-lg text-emerald-200 mb-8 leading-relaxed">
                  The deadline for Registration Area (Ward) and Polling Unit
                  fieldwork is approaching quickly. Skip the long queues and
                  technical confusion—we help you navigate the process in
                  minutes.
                </p>

                <h3 className="text-2xl font-bold mb-4 text-white">
                  Why Revalidate Now?
                </h3>
                <ul className="space-y-3 text-emerald-100 font-medium mb-10">
                  <li className="flex items-start">
                    <Info className="w-5 h-5 mr-3 text-amber-400 mt-1 flex-shrink-0" />{" "}
                    <span>
                      <strong className="text-white">Stay Eligible:</strong>{" "}
                      Unverified records will be deactivated from the National
                      Register.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 text-emerald-400 mt-1 flex-shrink-0" />{" "}
                    <span>
                      <strong className="text-white">Clean Records:</strong>{" "}
                      Remove errors, update your biometric data, and confirm
                      your polling unit.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Clock className="w-5 h-5 mr-3 text-emerald-400 mt-1 flex-shrink-0" />{" "}
                    <span>
                      <strong className="text-white">Avoid the Rush:</strong>{" "}
                      The LGA phase ends on May 2nd. Get yours done before the
                      fieldwork moves to the wards!
                    </span>
                  </li>
                </ul>

                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-8 py-4 bg-emerald-500 text-slate-900 rounded-full font-extrabold hover:bg-emerald-400 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.4)] text-lg"
                >
                  Start Your Revalidation Now{" "}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>

              <div className="lg:col-span-5">
                <div className="bg-slate-800/80 backdrop-blur-md rounded-3xl p-8 border border-slate-700 shadow-2xl relative">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <User className="w-24 h-24 text-emerald-500" />
                  </div>

                  <h3 className="text-xl font-bold mb-2 text-white relative z-10">
                    Deadline Countdown
                  </h3>
                  <p className="text-emerald-300 text-sm mb-4 relative z-10">
                    LGA Phase ends May 2nd, 2026
                  </p>

                  <div className="relative z-10 mb-8 border-b border-slate-700 pb-8">
                    <CountdownTimer />
                  </div>

                  <h3 className="text-xl font-bold mb-4 text-white relative z-10">
                    Our Assisted Revalidation Services:
                  </h3>
                  <p className="text-slate-300 text-sm mb-5 relative z-10">
                    We provide a seamless interface and expert support to ensure
                    your data is captured correctly on the official portal.
                  </p>

                  <ul className="space-y-4 text-slate-200 text-sm font-medium relative z-10 mb-8">
                    <li className="flex items-start">
                      <div className="bg-emerald-500/20 p-1.5 rounded-md mr-3 mt-0.5">
                        <CheckSquare className="w-4 h-4 text-emerald-400" />
                      </div>{" "}
                      <span>
                        <strong>Status Verification:</strong> We check if your
                        VIN is already cleared or needs immediate action.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-emerald-500/20 p-1.5 rounded-md mr-3 mt-0.5">
                        <CheckSquare className="w-4 h-4 text-emerald-400" />
                      </div>{" "}
                      <span>
                        <strong>Guided Online Filing:</strong> Step-by-step
                        assistance with the INEC CVR portal and facial
                        verification.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-emerald-500/20 p-1.5 rounded-md mr-3 mt-0.5">
                        <CheckSquare className="w-4 h-4 text-emerald-400" />
                      </div>{" "}
                      <span>
                        <strong>Appointment Scheduling:</strong> If needed, we
                        help you find the nearest LGA office and book your slot.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-emerald-500/20 p-1.5 rounded-md mr-3 mt-0.5">
                        <CheckSquare className="w-4 h-4 text-emerald-400" />
                      </div>{" "}
                      <span>
                        <strong>Instant Confirmation:</strong> Receive your
                        revalidation slip and digital proof of update.
                      </span>
                    </li>
                  </ul>

                  <div className="bg-slate-900/50 p-4 rounded-xl text-[10px] sm:text-xs text-slate-400 leading-relaxed border border-slate-700/50">
                    <strong>Disclaimer:</strong> Binary Digital Ventures is an
                    independent service provider. We facilitate the process via
                    the official INEC CVR portal. Official INEC services are
                    free; our fees cover administrative assistance and technical
                    support.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CAC SPOTLIGHT */}
      <section
        id="cac"
        className="py-20 bg-slate-900 text-white overflow-hidden relative"
      >
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="Corporate"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl blur opacity-30"></div>
              <div className="p-8 md:p-10 bg-slate-800/90 backdrop-blur-xl rounded-3xl border border-slate-700 shadow-2xl relative">
                <img
                  src="https://i.ibb.co/jP8RY8fY/CAC.png"
                  alt="CAC Logo"
                  className="absolute top-6 right-6 w-16 h-16 object-contain opacity-80"
                />
                <Building
                  className="w-16 h-16 text-green-400 mb-6"
                  strokeWidth={1.5}
                />
                <h3 className="text-2xl font-bold mb-6 text-white">
                  Full CAC Services:
                </h3>
                <ul className="space-y-4 text-slate-300 font-medium">
                  <li className="flex items-center bg-slate-900/50 p-3 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-400" />{" "}
                    Business Names
                  </li>
                  <li className="flex items-center bg-slate-900/50 p-3 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-400" />{" "}
                    Limited Liability Companies
                  </li>
                  <li className="flex items-center bg-slate-900/50 p-3 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-400" />{" "}
                    NGOs, Churches & Associations
                  </li>
                  <li className="flex items-center bg-slate-900/50 p-3 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-400" />{" "}
                    Annual Returns Filing
                  </li>
                  <li className="flex items-center bg-slate-900/50 p-3 rounded-lg border border-green-500/30">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-400" /> Full
                    DIY CAC Training Course
                  </li>
                </ul>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-slate-800 text-green-400 text-sm font-bold mb-6 border border-slate-700">
                <img
                  src="https://i.ibb.co/jP8RY8fY/CAC.png"
                  alt="CAC"
                  className="w-5 h-5 mr-2 object-contain"
                />{" "}
                CAC Spotlight
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight">
                Registering a Business Does Not Have to Be Complicated
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Whether you want to register a business name, open a limited
                liability company, register a church, NGO or association, or
                file your annual returns, we handle the entire process for you.
              </p>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                You do not need to figure out the CAC portal or waste days going
                back and forth. Just come to us. We have also put together a
                full CAC registration course for anyone who wants to learn how
                to do it themselves.
              </p>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-8 py-4 bg-green-500 text-slate-900 rounded-full font-bold hover:bg-green-400 transition-colors shadow-[0_0_15px_rgba(34,197,94,0.3)]"
              >
                Register Your Business Now{" "}
                <ChevronRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CBT SPOTLIGHT */}
      <section id="cbt" className="py-20 bg-slate-100 relative overflow-hidden">
        {/* Decorative background shapes */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-slate-200/50 rounded-l-[100px] -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-slate-800 text-green-400 text-sm font-bold mb-6 shadow-sm border border-slate-700">
                <img
                  src="https://i.ibb.co/YBL7TgnD/Official-JAMB-logo.png"
                  alt="JAMB Logo"
                  className="w-5 h-5 mr-2 object-contain"
                />{" "}
                JAMB CBT Practice Facility
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
                Do Not Walk Into That JAMB Hall Without Practising First
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed font-medium">
                Most students fail JAMB not because they did not read but
                because they have never sat in front of a computer and taken a
                timed exam before.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed font-medium">
                Our CBT practice sessions give you the real experience before
                the real day. Timed. Pressured. Exactly like the actual exam.
                Come and practise. Walk into that hall ready.
              </p>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-8 py-4 bg-slate-900 text-white rounded-full font-bold hover:bg-slate-800 transition-colors shadow-lg"
              >
                Book a CBT Session{" "}
                <ChevronRight className="ml-2 w-5 h-5 text-green-400" />
              </a>
            </div>

            <div className="relative">
              <div className="aspect-square max-w-md mx-auto relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white bg-white">
                <img
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Computer Lab"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/20"></div>

                {/* Overlay badge */}
                <div className="absolute -bottom-6 -left-6 w-32 h-32 md:w-36 md:h-36 bg-green-500 rounded-full flex flex-col items-center justify-center shadow-xl border-4 border-white text-slate-900 transform translate-x-10 translate-y-[-2rem]">
                  <img
                    src="https://i.ibb.co/YBL7TgnD/Official-JAMB-logo.png"
                    alt="JAMB Logo"
                    className="w-10 h-10 md:w-12 md:h-12 mb-1 object-contain"
                  />
                  <span className="font-bold text-xs md:text-sm text-center leading-tight">
                    JAMB
                    <br />
                    Standard
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Why People in Orlu & Imo State Choose Us
            </h2>
            <p className="text-lg text-slate-600 font-medium">
              We don't just process documents; we deliver peace of mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 max-w-5xl mx-auto">
            {reasons.map((reason, index) => {
              const [boldText, normalText] = reason.split(" — ");
              return (
                <div
                  key={index}
                  className="flex bg-slate-50 p-6 rounded-2xl border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex-shrink-0 mr-5">
                    <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center text-green-400 shadow-sm">
                      <CheckCircle size={24} strokeWidth={2.5} />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">
                      {boldText}
                    </h4>
                    <p className="text-slate-600 leading-relaxed font-medium">
                      {normalText}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT / CTA SECTION */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-green-500 rounded-full opacity-20 blur-[100px]"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-emerald-500 rounded-full opacity-20 blur-[100px]"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-5xl font-extrabold mb-8 text-white">
                  Come and See Us or Reach Out
                </h2>
                <div className="space-y-8 mb-8">
                  <div className="flex items-start">
                    <div className="bg-slate-800 p-3 rounded-xl mr-5">
                      <MapPin className="w-6 h-6 text-green-400 flex-shrink-0" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-white">
                        Office Address
                      </h4>
                      <p className="text-slate-300 mt-1">
                        No. 169 B.S.C. Road, Orlu, Imo State
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-slate-800 p-3 rounded-xl mr-5">
                      <Clock className="w-6 h-6 text-green-400 flex-shrink-0" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-white">
                        Working Hours
                      </h4>
                      <p className="text-slate-300 mt-1">
                        Monday to Saturday: 8:00 AM to 5:30 PM
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-slate-800 p-3 rounded-xl mr-5">
                      <Phone className="w-6 h-6 text-green-400 flex-shrink-0" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-white">
                        Contact Number
                      </h4>
                      <p className="text-slate-300 mt-1">+{WHATSAPP_NUMBER}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800 p-8 md:p-10 rounded-2xl text-center shadow-2xl border border-slate-700 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-500 opacity-10 rounded-bl-full"></div>
                <MessageCircle className="w-16 h-16 text-green-400 mx-auto mb-6 relative z-10" />
                <h3 className="text-3xl font-extrabold mb-4 text-white relative z-10">
                  Need Service Now?
                </h3>
                <p className="text-slate-300 mb-8 font-medium relative z-10">
                  Click below to send us a direct message on WhatsApp. We reply
                  fast.
                </p>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-center justify-center px-8 py-4 bg-green-500 hover:bg-green-400 text-slate-900 rounded-full font-extrabold text-lg transition-all shadow-[0_0_20px_rgba(34,197,94,0.3)] relative z-10"
                >
                  Chat Us on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <div className="mb-6 md:mb-0">
            <div className="flex items-center justify-center md:justify-start mb-3">
              <div className="w-8 h-8 bg-slate-800 rounded mr-2 flex items-center justify-center border border-slate-700">
                <span className="text-green-400 font-bold text-sm">
                  <Monitor size={16} />
                </span>
              </div>
              <span className="text-white font-bold text-xl tracking-tight">
                Binary Digital Ventures
              </span>
            </div>
            <p className="text-sm font-medium">
              © 2026 Binary Digital Solutions Ventures. RC IM11913.
            </p>
            <p className="text-sm font-medium mt-1">
              All rights reserved. Orlu, Imo State, Nigeria.
            </p>
          </div>
          <div className="text-green-500 font-extrabold italic text-lg tracking-wider">
            Fast. Affordable. Done Right.
          </div>
        </div>
      </footer>
    </div>
  );
}
